// The embedded widget's colour themes.
//
// Copied verbatim from the THEMES table in
// `Report Management Service/public/widget.bundle.js`, which is the thing a visitor
// actually sees. The builder's preview has to paint from the same numbers or it is a
// picture of a widget that does not exist — which is exactly what it was before.
//
// If the bundle's table changes, change this one. There is no build step shared between
// the two, so the only thing keeping them honest is that they are both short and both
// say so.

export const WIDGET_THEMES = {
  light: {
    primary: "#8b5cf6", primaryDark: "#7c3aed", primaryLight: "#a78bfa",
    secondary: "#06b6d4", success: "#10b981", warning: "#f59e0b", danger: "#ef4444",
    bg: "#ffffff", bgSecondary: "#f8fafc", text: "#0f172a", textLight: "#64748b",
    border: "#e2e8f0", accent: "#ec4899", surface: "#ffffff",
  },
  dark: {
    primary: "#a78bfa", primaryDark: "#8b5cf6", primaryLight: "#c4b5fd",
    secondary: "#22d3ee", success: "#34d399", warning: "#fbbf24", danger: "#f87171",
    bg: "#0f172a", bgSecondary: "#1e293b", text: "#f1f5f9", textLight: "#94a3b8",
    border: "#334155", accent: "#f472b6", surface: "#1e293b",
  },
  cyber: {
    primary: "#00ff88", primaryDark: "#00cc6a", primaryLight: "#33ff9f",
    secondary: "#7b61ff", success: "#00ff88", warning: "#ffcc00", danger: "#ff3366",
    bg: "#0a0a0f", bgSecondary: "#151522", text: "#ffffff", textLight: "#a0a0c0",
    border: "#2a2a3a", accent: "#ff00ff", surface: "#151522",
  },
};

// The pickable list, in the order the builder shows it. Derived from the table above so
// the swatches on the Layout tab cannot disagree with the preview beside them.
export const THEME_OPTIONS = [
  { id: "light", name: "Light" },
  { id: "dark", name: "Dark" },
  { id: "cyber", name: "Cyber" },
].map((t) => ({ ...t, ...WIDGET_THEMES[t.id] }));

// The same custom properties the bundle's applyTheme() writes onto the document, as a
// style object for one element instead. Camel-cased keys become the kebab-cased custom
// properties the CSS reads: textLight -> --text-light.
const kebab = (k) => k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

// Colour maths, so a single custom colour can supply the family of variables that
// actually paints the widget. Mirrored in widget.bundle.js's applyTheme().
const toRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? [1, 2, 3].map((i) => parseInt(m[i], 16)) : null;
};
const toHex = (rgb) => `#${rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0")).join("")}`;

// `t` of the way from a to b. Used instead of opacity because these land on custom
// properties that end up in `border`, `background` and `color` alike.
const mix = (a, b, t) => {
  const [ra, rb] = [toRgb(a), toRgb(b)];
  if (!ra || !rb) return a;
  return toHex(ra.map((c, i) => c + (rb[i] - c) * t));
};

// How one Report colors field spreads across the palette.
//
// Overriding `primary` alone left the widget looking half-changed: its headings and CTA
// are `linear-gradient(primary -> secondary)`, so a custom brand colour produced a
// gradient that was still half the theme's purple. Same story for `text`: nearly every
// line of copy in the bundle is `--text-light`, not `--text`, so setting Text colour
// visibly changed almost nothing.
//
// So each field supplies a small family rather than a single variable, and the shades
// are derived so hover states and secondary copy keep their depth instead of flattening
// into one block of colour.
export function overrideVars(theme, colors) {
  const out = {};
  const val = (k) => (colors?.[k] || "").trim();

  const primary = val("primary");
  if (primary) {
    out.primary = primary;
    out.primaryDark = mix(primary, "#000000", 0.18);
    out.primaryLight = mix(primary, "#ffffff", 0.25);
    // The far end of every brand gradient. Kept a shade off `primary` rather than equal
    // to it, so the gradients stay gradients.
    out.secondary = mix(primary, "#ffffff", 0.35);
  }

  const background = val("background");
  if (background) {
    // `surface` too: leaving the card on the theme's surface while the page behind it
    // changes produces a panel that looks like a rendering fault.
    out.bg = background;
    out.surface = background;
    out.bgSecondary = mix(background, theme.text, 0.06);
    out.border = mix(background, theme.text, 0.14);
  }

  // The domain field alone — the box the visitor types into, not the card around it and
  // not the button under it. Applied last so it wins over the shade `background` derives
  // for --bg-secondary, and defaulted to nothing so the theme keeps painting the field
  // when it is blank.
  const inputBackground = val("inputBackground");
  if (inputBackground) {
    out.inputBg = inputBackground;
  }

  const text = val("text");
  if (text) {
    out.text = text;
    // Secondary copy: the same hue faded toward whatever the background ends up being,
    // so it reads as muted rather than as a second unrelated colour.
    out.textLight = mix(text, background || theme.bg, 0.4);
  }

  return out;
}

// The values the Report colors pickers show when a field is blank — what that colour is
// *currently* resolving to. An empty swatch defaulting to grey told the user their theme
// was grey.
export const themeDefaults = (themeId) => {
  const t = WIDGET_THEMES[themeId] || WIDGET_THEMES.light;
  return { primary: t.primary, background: t.bg, inputBackground: t.bgSecondary, text: t.text };
};

export function themeVars(themeId, colors) {
  const theme = WIDGET_THEMES[themeId] || WIDGET_THEMES.light;
  const vars = {};
  Object.entries(theme).forEach(([key, value]) => { vars[`--${kebab(key)}`] = value; });

  // Blank means "inherit the theme", which is the default for every widget saved so far —
  // so an empty field must never write an empty custom property over a good one.
  Object.entries(overrideVars(theme, colors)).forEach(([key, value]) => {
    vars[`--${kebab(key)}`] = value;
  });

  const rgb = toRgb(vars["--primary"]);
  if (rgb) vars["--primary-rgb"] = rgb.join(", ");

  return vars;
}

// Relative luminance, WCAG 2.x. Used only to warn — a widget lives on someone else's
// marketing site, and two free-form colours can quietly produce white on white there.
const luminance = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  const [r, g, b] = [1, 2, 3].map((i) => {
    const c = parseInt(m[i], 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// The effective text/background pair once the overrides are applied, and whether it
// clears WCAG AA for body text.
export function contrastCheck(themeId, colors) {
  const theme = WIDGET_THEMES[themeId] || WIDGET_THEMES.light;
  const text = (colors?.text || "").trim() || theme.text;
  const bg = (colors?.background || "").trim() || theme.bg;
  const ratio = contrastRatio(text, bg);
  return { text, bg, ratio, passes: ratio === null ? true : ratio >= 4.5 };
}
